/**
 * Renders an assistant answer to HTML.
 *
 * The model is asked for markdown links and occasionally emphasis, which is a
 * small enough subset to handle directly — pulling in a markdown library to
 * render three constructs would cost more bundle than the widget itself.
 *
 * ## Why this escapes first and whitelists the host
 *
 * The output goes through `{@html}`, so it is the one place on this site where
 * model output becomes markup. Two rules make that safe rather than merely
 * unlikely to break:
 *
 * 1. **Escape everything first**, then add the handful of tags this function
 *    generates. Nothing the model wrote can open a tag, because `<` is already
 *    `&lt;` by the time any pattern runs.
 * 2. **Only our own URLs become links.** The system prompt tells the assistant to
 *    cite URLs from the knowledge pack and never to construct one, but a prompt
 *    is an instruction, not a guarantee — a visitor who talked it into emitting
 *    a link elsewhere would otherwise get a clickable one, published under our
 *    domain and our name. Anything off-site is rendered as its label alone.
 */

const SITE_ORIGIN = 'https://dutchpassion.co.th';

const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

/**
 * Whether a URL is one of ours, and therefore safe to make clickable.
 *
 * Site-relative paths pass; absolute URLs must be on the canonical origin. Note
 * this runs on the *escaped* string, so an ampersand in a query string arrives
 * as `&amp;` — decoded back for the comparison only.
 */
function ourUrl(href: string): string | null {
	const decoded = href.replace(/&amp;/g, '&');

	if (decoded.startsWith('/')) return decoded;

	try {
		return new URL(decoded).origin === SITE_ORIGIN ? decoded : null;
	} catch {
		return null;
	}
}

export function renderAnswer(text: string): string {
	let html = escapeHtml(text);

	// [label](url) — a link when it points at us, its own label when it does not.
	html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, label: string, href: string) => {
		const safe = ourUrl(href);
		return safe ? `<a href="${safe}">${label}</a>` : label;
	});

	html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');

	// Bare list markers, which the model uses when recommending several strains.
	html = html.replace(/^[-*]\s+/gm, '• ');

	return html.replace(/\n/g, '<br />');
}
